import { Injectable } from '@angular/core';
import { AppNotificationUtilClass, MachineMaintenanceDataService } from '@monorepo/services';
import {
  TREQUEST_ADD_MACHINE_NEEDLES,
  TREQUEST_ADD_NEW_WORKSTATION,
  TREQUEST_ASSIGN_MACHINE_TO_WORKSTATION,
  TREQUEST_REMOVE_MACHINE_FROM_WORKSTATION,
  TREQUEST_REMOVE_MACHINE_NEEDLES,
  TREQUEST_REMOVE_WORKSTATION,
  TREQUEST_REPLACE_MACHINE_FROM_WORKSTATION,
  TREQUEST_RESET_FACTORY_PRODUCTION_AREA,
  TRESPONSE_GET_FLOATING_AREA_MCHINES_BY_LOCATION,
  TRESPONSE_LOCATION_LIST,
} from '@monorepo/types';
import { select, Store } from '@ngrx/store';
import { catchError, lastValueFrom, of } from 'rxjs';
import { TMachineMaintenanceState } from '../reducers';
import * as LineSetupActions from './line-setup.actions';
import {
  TLineSetupActionsAssignMachineFetchPayload,
  TLineSetupActionsRemoveMachineFetchPayload,
  TLineSetupActionsEditWorkstationFetchPayload,
  TLineSetupActionsReplaceMachineFetchPayload,
} from './line-setup.models';
import * as LineSetupSelectors from './line-setup.selectors';

@Injectable({
  providedIn: 'root',
})
export class LineSetupFacade {
  constructor(
    private store: Store<TMachineMaintenanceState>,
    private mmDataService: MachineMaintenanceDataService,
    private appNotificationUtilClass: AppNotificationUtilClass,
  ) {}

  fetchFactoryLineWorkstationSummaryList() {
    this.store.dispatch(new LineSetupActions.GetFactoryLineWorkstationSummaryList());
  }

  addNewWorkstation(payload: TREQUEST_ADD_NEW_WORKSTATION, fetchPayload: TLineSetupActionsEditWorkstationFetchPayload) {
    this.store.dispatch(new LineSetupActions.AddNewWorkstation(payload, fetchPayload));
  }

  getAllWorkstationsForProductionArea(locationId: TRESPONSE_LOCATION_LIST['locationDetails'][number]['id']) {
    this.store.dispatch(new LineSetupActions.GetAllWorkstationsForProductionArea(locationId));
  }

  assignMachineToWorkstation(
    payload: TREQUEST_ASSIGN_MACHINE_TO_WORKSTATION,
    fetchPayload: TLineSetupActionsAssignMachineFetchPayload,
  ) {
    this.store.dispatch(new LineSetupActions.AssignMachineToWorkstation(payload, fetchPayload));
  }

  removeMachineFromWorkstation(
    payload: TREQUEST_REMOVE_MACHINE_FROM_WORKSTATION,
    fetchPayload: TLineSetupActionsRemoveMachineFetchPayload,
  ) {
    this.store.dispatch(new LineSetupActions.RemoveMachineFromWorkstation(payload, fetchPayload));
  }

  replaceMachine(
    payload: TREQUEST_REPLACE_MACHINE_FROM_WORKSTATION,
    fetchPayload: TLineSetupActionsReplaceMachineFetchPayload,
  ) {
    this.store.dispatch(new LineSetupActions.ReplaceMachine(payload, fetchPayload));
  }

  removeWorkstation(payload: TREQUEST_REMOVE_WORKSTATION, fetchPayload: TLineSetupActionsEditWorkstationFetchPayload) {
    this.store.dispatch(new LineSetupActions.RemoveWorkstation(payload, fetchPayload));
  }

  resetFactoryProductionArea(payload: Omit<TREQUEST_RESET_FACTORY_PRODUCTION_AREA, 'factoryId'>) {
    this.store.dispatch(new LineSetupActions.ResetFactoryProductionArea(payload));
  }

  fetchFactoryLinesForShiftManagement() {
    this.store.dispatch(new LineSetupActions.GetFactoryLinesForShiftManagement());
  }

  addMachineNeedle(payload: TREQUEST_ADD_MACHINE_NEEDLES) {
    this.store.dispatch(new LineSetupActions.AddMachineNeedles(payload));
  }

  removeMachineNeedle(payload: TREQUEST_REMOVE_MACHINE_NEEDLES) {
    this.store.dispatch(new LineSetupActions.RemoveMachineNeedles(payload));
  }

  getFactoryLinesForShiftManagement() {
    return this.store.pipe(select(LineSetupSelectors.getFactoryLinesForShiftManagement));
  }

  getFactoryLineWorkstationSummaryList() {
    return this.store.pipe(select(LineSetupSelectors.getFactoryLineWorkstationSummaryList));
  }

  getProductionAreaWorkStations() {
    return this.store.pipe(select(LineSetupSelectors.getProductionAreaWorkStations));
  }

  async getFloatingAreaMachinesByLocations(
    locationIds: number[],
  ): Promise<(TRESPONSE_GET_FLOATING_AREA_MCHINES_BY_LOCATION | null)[]> {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    try {
      return await Promise.all(
        locationIds.map((locationId) =>
          lastValueFrom(
            this.mmDataService.getFloatingAreaMachinesByLocation(locationId).pipe(catchError(() => of(null))),
          ),
        ),
      );
    } finally {
      this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    }
  }
}

export type TGetProductionAreaWorkStations = ReturnType<LineSetupFacade['getProductionAreaWorkStations']>;
export type TGetFactoryLineWorkstationSummaryList = ReturnType<LineSetupFacade['getFactoryLineWorkstationSummaryList']>;
export type TGetFactoryLinesForShiftManagement = ReturnType<LineSetupFacade['getFactoryLinesForShiftManagement']>;
